import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

gsap?.registerPlugin(ScrollTrigger);

const DemoSection = () => {
  const [language, setLanguage] = useState('en');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    businessType: '',
    teamSize: '',
    currentBookings: '',
    preferredTime: '',
    phone: '',
    challenges: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const sectionRef = useRef(null);
  const formRef = useRef(null);
  const calendarRef = useRef(null);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setLanguage(savedLanguage);
  }, []);

  useEffect(() => {
    const ctx = gsap?.context(() => {
      gsap?.fromTo(
        [formRef?.current, calendarRef?.current],
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          stagger: 0.2,
          scrollTrigger: {
            trigger: sectionRef?.current,
            start: 'top 70%',
            toggleActions: 'play none none reverse',
          },
        }
      );
    }, sectionRef);

    return () => ctx?.revert();
  }, []);

  const content = {
    en: {
      title: 'Book Your Personalized Demo',
      subtitle:
        'See exactly how Slotyfi can transform your booking process in a live, personalized demonstration',
      form: {
        name: 'Full Name',
        email: 'Business Email',
        company: 'Company Name',
        businessType: 'Business Type',
        teamSize: 'Team Size',
        currentBookings: 'Monthly Bookings',
        preferredTime: 'Preferred Demo Time',
        phone: 'Phone Number',
        challenges: 'Current Challenges',
        challengesPlaceholder:
          'Tell us about your biggest booking challenges...',
      },
      businessTypes: [
        { value: 'beauty', label: 'Beauty & Wellness' },
        { value: 'healthcare', label: 'Healthcare & Medical' },
        { value: 'fitness', label: 'Fitness & Sports' },
        { value: 'consulting', label: 'Consulting & Professional Services' },
        { value: 'education', label: 'Education & Training' },
        { value: 'automotive', label: 'Automotive Services' },
        { value: 'home-services', label: 'Home Services' },
        { value: 'other', label: 'Other' },
      ],
      teamSizes: [
        { value: '1-5', label: '1-5 employees' },
        { value: '6-15', label: '6-15 employees' },
        { value: '16-50', label: '16-50 employees' },
        { value: '51-100', label: '51-100 employees' },
        { value: '100+', label: '100+ employees' },
      ],
      bookingVolumes: [
        { value: '0-50', label: '0-50 bookings' },
        { value: '51-200', label: '51-200 bookings' },
        { value: '201-500', label: '201-500 bookings' },
        { value: '501-1000', label: '501-1000 bookings' },
        { value: '1000+', label: '1000+ bookings' },
      ],
      demoTimes: [
        { value: 'morning', label: 'Morning (9 AM - 12 PM)' },
        { value: 'afternoon', label: 'Afternoon (12 PM - 5 PM)' },
        { value: 'evening', label: 'Evening (5 PM - 8 PM)' },
        { value: 'flexible', label: "I'm flexible" },
      ],
      submitButton: 'Book My Demo',
      submittingButton: 'Booking Demo...',
      benefits: [
        {
          icon: 'Users',
          title: 'Personalized Experience',
          description:
            'Demo tailored to your specific business type and challenges',
        },
        {
          icon: 'Clock',
          title: '30-Minute Session',
          description:
            'Comprehensive walkthrough of features that matter to you',
        },
        {
          icon: 'Shield',
          title: 'No Commitment',
          description: 'Free consultation with no pressure or obligations',
        },
        {
          icon: 'Headphones',
          title: 'Expert Guidance',
          description:
            'Direct access to our product specialists and setup support',
        },
      ],
      calendar: {
        title: 'Available Demo Slots',
        timezone: 'All times shown in your local timezone',
        today: 'Today',
        tomorrow: 'Tomorrow',
      },
      success: {
        title: 'Demo Booked Successfully!',
        subtitle:
          "We're excited to show you how Slotyfi can transform your business",
        nextSteps: 'What happens next:',
        steps: [
          "You'll receive a calendar invitation within 5 minutes",
          'Our team will prepare a customized demo based on your information',
          "We'll send you preparation materials 24 hours before the demo",
          "During the demo, you'll see live examples using your business scenario",
        ],
        contact:
          'Questions? Contact us at demo@slotyfi.com or call +1 (555) 123-4567',
      },
    },
    fa: {
      title: 'دمو شخصی‌سازی شده خود را رزرو کنید',
      subtitle:
        'دقیقاً ببینید اسلاتیفای چگونه می‌تواند فرآیند رزرو شما را در یک نمایش زنده و شخصی‌سازی شده تغییر دهد',
      form: {
        name: 'نام کامل',
        email: 'ایمیل کسب‌وکار',
        company: 'نام شرکت',
        businessType: 'نوع کسب‌وکار',
        teamSize: 'اندازه تیم',
        currentBookings: 'رزروهای ماهانه',
        preferredTime: 'زمان ترجیحی دمو',
        phone: 'شماره تلفن',
        challenges: 'چالش‌های فعلی',
        challengesPlaceholder:
          'درباره بزرگترین چالش‌های رزرو خود به ما بگویید...',
      },
      businessTypes: [
        { value: 'beauty', label: 'زیبایی و سلامت' },
        { value: 'healthcare', label: 'بهداشت و درمان' },
        { value: 'fitness', label: 'تناسب اندام و ورزش' },
        { value: 'consulting', label: 'مشاوره و خدمات حرفه‌ای' },
        { value: 'education', label: 'آموزش و تربیت' },
        { value: 'automotive', label: 'خدمات خودرو' },
        { value: 'home-services', label: 'خدمات منزل' },
        { value: 'other', label: 'سایر' },
      ],
      teamSizes: [
        { value: '1-5', label: '1-5 کارمند' },
        { value: '6-15', label: '6-15 کارمند' },
        { value: '16-50', label: '16-50 کارمند' },
        { value: '51-100', label: '51-100 کارمند' },
        { value: '100+', label: '100+ کارمند' },
      ],
      bookingVolumes: [
        { value: '0-50', label: '0-50 رزرو' },
        { value: '51-200', label: '51-200 رزرو' },
        { value: '201-500', label: '201-500 رزرو' },
        { value: '501-1000', label: '501-1000 رزرو' },
        { value: '1000+', label: '1000+ رزرو' },
      ],
      demoTimes: [
        { value: 'morning', label: 'صبح (9 صبح - 12 ظهر)' },
        { value: 'afternoon', label: 'بعدازظهر (12 ظهر - 5 عصر)' },
        { value: 'evening', label: 'عصر (5 عصر - 8 شب)' },
        { value: 'flexible', label: 'انعطاف‌پذیر هستم' },
      ],
      submitButton: 'دمو من را رزرو کن',
      submittingButton: 'در حال رزرو دمو...',
      benefits: [
        {
          icon: 'Users',
          title: 'تجربه شخصی‌سازی شده',
          description: 'دمو متناسب با نوع کسب‌وکار و چالش‌های خاص شما',
        },
        {
          icon: 'Clock',
          title: 'جلسه 30 دقیقه‌ای',
          description: 'بررسی جامع ویژگی‌هایی که برای شما مهم هستند',
        },
        {
          icon: 'Shield',
          title: 'بدون تعهد',
          description: 'مشاوره رایگان بدون فشار یا تعهدات',
        },
        {
          icon: 'Headphones',
          title: 'راهنمایی متخصص',
          description: 'دسترسی مستقیم به متخصصان محصول و پشتیبانی راه‌اندازی',
        },
      ],
      calendar: {
        title: 'زمان‌های دمو موجود',
        timezone: 'همه زمان‌ها در منطقه زمانی محلی شما نشان داده شده',
        today: 'امروز',
        tomorrow: 'فردا',
      },
      success: {
        title: 'دمو با موفقیت رزرو شد!',
        subtitle:
          'ما هیجان‌زده‌ایم که نشان دهیم اسلاتیفای چگونه می‌تواند کسب‌وکار شما را تغییر دهد',
        nextSteps: 'مراحل بعدی:',
        steps: [
          'ظرف 5 دقیقه دعوتنامه تقویم دریافت خواهید کرد',
          'تیم ما دمو سفارشی بر اساس اطلاعات شما آماده خواهد کرد',
          '24 ساعت قبل از دمو مواد آمادگی برای شما ارسال خواهیم کرد',
          'در طول دمو، نمونه‌های زنده با استفاده از سناریو کسب‌وکار شما خواهید دید',
        ],
        contact:
          'سوال دارید؟ با ما تماس بگیرید demo@slotyfi.com یا تماس بگیرید +1 (555) 123-4567',
      },
    },
  };

  const currentContent = content?.[language];

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 2000));

    setIsSubmitting(false);
    setIsSubmitted(true);

    // Animate success state
    gsap?.fromTo(
      sectionRef?.current,
      { scale: 0.95, opacity: 0.8 },
      { scale: 1, opacity: 1, duration: 0.8, ease: 'back.out(1.7)' }
    );
  };

  const mockCalendarSlots = [
    { time: '9:00 AM', available: true, date: 'today' },
    { time: '10:30 AM', available: false, date: 'today' },
    { time: '2:00 PM', available: true, date: 'today' },
    { time: '3:30 PM', available: true, date: 'today' },
    { time: '9:00 AM', available: true, date: 'tomorrow' },
    { time: '11:00 AM', available: true, date: 'tomorrow' },
    { time: '1:30 PM', available: false, date: 'tomorrow' },
    { time: '4:00 PM', available: true, date: 'tomorrow' },
  ];

  if (isSubmitted) {
    return (
      <section
        ref={sectionRef}
        className="py-20 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <div className="bg-white rounded-3xl p-12 shadow-xl">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="CheckCircle" size={40} color="#059669" />
              </div>

              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                {currentContent?.success?.title}
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                {currentContent?.success?.subtitle}
              </p>

              <div
                className={`${
                  language === 'fa' ? 'text-right' : 'text-left'
                } mb-8`}>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {currentContent?.success?.nextSteps}
                </h3>
                <div className="space-y-3">
                  {currentContent?.success?.steps?.map((step, index) => (
                    <div key={index} className="flex items-start">
                      <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                        <span className="text-blue-600 text-sm font-medium">
                          {index + 1}
                        </span>
                      </div>
                      <p className="text-gray-700">{step}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-800">
                  {currentContent?.success?.contact}
                </p>
              </div>

              <Button
                variant="outline"
                size="lg"
                onClick={() => setIsSubmitted(false)}
                iconName="ArrowLeft"
                iconPosition="left">
                {language === 'en' ? 'Book Another Demo' : 'رزرو دمو دیگر'}
              </Button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      id="demo"
      className="py-20 bg-gradient-to-br from-blue-50 to-purple-50">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div
          className={`text-center mb-16 ${language === 'fa' ? 'rtl' : 'ltr'}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {currentContent?.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {currentContent?.subtitle}
          </p>
        </div>

        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Demo Form */}
            <div ref={formRef} className="bg-white rounded-2xl p-8 shadow-xl">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <Input
                    label={currentContent?.form?.name}
                    type="text"
                    required
                    value={formData?.name}
                    onChange={(e) =>
                      handleInputChange('name', e?.target?.value)
                    }
                    placeholder="John Smith"
                  />

                  <Input
                    label={currentContent?.form?.email}
                    type="email"
                    required
                    value={formData?.email}
                    onChange={(e) =>
                      handleInputChange('email', e?.target?.value)
                    }
                    placeholder="john@company.com"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <Input
                    label={currentContent?.form?.company}
                    type="text"
                    required
                    value={formData?.company}
                    onChange={(e) =>
                      handleInputChange('company', e?.target?.value)
                    }
                    placeholder="Your Company Name"
                  />

                  <Select
                    label={currentContent?.form?.businessType}
                    required
                    options={currentContent?.businessTypes}
                    value={formData?.businessType}
                    onChange={(value) =>
                      handleInputChange('businessType', value)
                    }
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <Select
                    label={currentContent?.form?.teamSize}
                    required
                    options={currentContent?.teamSizes}
                    value={formData?.teamSize}
                    onChange={(value) => handleInputChange('teamSize', value)}
                  />

                  <Select
                    label={currentContent?.form?.currentBookings}
                    required
                    options={currentContent?.bookingVolumes}
                    value={formData?.currentBookings}
                    onChange={(value) =>
                      handleInputChange('currentBookings', value)
                    }
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <Input
                    label={currentContent?.form?.phone}
                    type="tel"
                    value={formData?.phone}
                    onChange={(e) =>
                      handleInputChange('phone', e?.target?.value)
                    }
                    placeholder="+1 (555) 123-4567"
                  />

                  <Select
                    label={currentContent?.form?.preferredTime}
                    required
                    options={currentContent?.demoTimes}
                    value={formData?.preferredTime}
                    onChange={(value) =>
                      handleInputChange('preferredTime', value)
                    }
                  />
                </div>

                <Input
                  label={currentContent?.form?.challenges}
                  type="text"
                  value={formData?.challenges}
                  onChange={(e) =>
                    handleInputChange('challenges', e?.target?.value)
                  }
                  placeholder={currentContent?.form?.challengesPlaceholder}
                />

                <Button
                  type="submit"
                  variant="default"
                  size="lg"
                  loading={isSubmitting}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  iconName="Calendar"
                  iconPosition="left">
                  {isSubmitting
                    ? currentContent?.submittingButton
                    : currentContent?.submitButton}
                </Button>
              </form>
            </div>

            {/* Calendar & Benefits */}
            <div className="space-y-8">
              {/* Available Slots */}
              <div
                ref={calendarRef}
                className="bg-white rounded-2xl p-8 shadow-xl">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {currentContent?.calendar?.title}
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  {currentContent?.calendar?.timezone}
                </p>

                <div className="space-y-6">
                  {/* Today */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">
                      {currentContent?.calendar?.today}
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      {mockCalendarSlots
                        ?.filter((slot) => slot?.date === 'today')
                        ?.map((slot, index) => (
                          <button
                            key={index}
                            disabled={!slot?.available}
                            className={`p-3 rounded-lg text-sm font-medium transition-colors ${
                              slot?.available
                                ? 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            }`}>
                            {slot?.time}
                          </button>
                        ))}
                    </div>
                  </div>

                  {/* Tomorrow */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">
                      {currentContent?.calendar?.tomorrow}
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      {mockCalendarSlots
                        ?.filter((slot) => slot?.date === 'tomorrow')
                        ?.map((slot, index) => (
                          <button
                            key={index}
                            disabled={!slot?.available}
                            className={`p-3 rounded-lg text-sm font-medium transition-colors ${
                              slot?.available
                                ? 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            }`}>
                            {slot?.time}
                          </button>
                        ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Benefits */}
              <div className="bg-white rounded-2xl p-8 shadow-xl">
                <h3 className="text-xl font-bold text-gray-900 mb-6">
                  {language === 'en'
                    ? 'What to Expect'
                    : 'چه انتظاری داشته باشید'}
                </h3>
                <div className="space-y-4">
                  {currentContent?.benefits?.map((benefit, index) => (
                    <div key={index} className="flex items-start">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                        <Icon name={benefit?.icon} size={20} color="#2563EB" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">
                          {benefit?.title}
                        </h4>
                        <p className="text-gray-600 text-sm">
                          {benefit?.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DemoSection;
